import { useState, useEffect } from "react";
import { motion } from "framer-motion";
import { Menu, X } from "lucide-react";
import { Button } from "@/components/ui/button";
import { useNavigate } from "react-router-dom";
import { useContent } from "@/hooks/useContent";

const Header = () => {
  const [isScrolled, setIsScrolled] = useState(false);
  const [isMobileMenuOpen, setIsMobileMenuOpen] = useState(false);
  const [isAdmin, setIsAdmin] = useState(false);
  const navigate = useNavigate();
  const { content: c } = useContent();

  useState(() => {
    const handleScroll = () => {
      setIsScrolled(window.scrollY > 50);
    };
    window.addEventListener("scroll", handleScroll);
    return () => window.removeEventListener("scroll", handleScroll);
  }, []);

  useEffect(() => {
    const token = localStorage.getItem("adminToken");
    setIsAdmin(!!token);
  }, []);

  const contactEmail = c.contact_email || "info@kspaceproperties.com";

  return (
    <header 
      className="fixed top-0 left-0 right-0 z-50 bg-white/95 backdrop-blur-md shadow-md transition-all duration-300"
      data-testid="header"
    >
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="flex items-center justify-between h-28">
          <motion.div 
            initial={{ opacity: 0, x: -20 }}
            animate={{ opacity: 1, x: 0 }}
            className="cursor-pointer flex items-center"
            onClick={() => navigate('/')}
          >
            <img 
              src="https://customer-assets.emergentagent.com/job_kspace-emerald-green/artifacts/3dnddvid_KS_Space_Properties_Logo.png"
              alt="KSpace Properties Logo"
              className="h-20 w-auto"
              data-testid="header-logo"
            />
          </motion.div>

          {/* Desktop Navigation */}
          <nav className="hidden md:flex items-center gap-8">
            <a 
              href="#"
              onClick={(e) => { e.preventDefault(); navigate('/'); }}
              className="font-medium transition-colors border border-primary text-primary px-4 py-2 rounded-full hover:bg-accent hover:text-white hover:border-accent active:bg-accent active:text-white active:border-accent"
            >
              Home
            </a>
            <a 
              href="#"
              onClick={(e) => { e.preventDefault(); navigate('/terms'); }}
              className="font-medium transition-colors border border-primary text-primary px-4 py-2 rounded-full hover:bg-accent hover:text-white hover:border-accent active:bg-accent active:text-white active:border-accent"
            >
              Terms
            </a>
            <a 
              href="#"
              onClick={(e) => { e.preventDefault(); navigate('/privacy'); }}
              className="font-medium transition-colors border border-primary text-primary px-4 py-2 rounded-full hover:bg-accent hover:text-white hover:border-accent active:bg-accent active:text-white active:border-accent"
            >
              Privacy
            </a>
            <a 
              href={`mailto:${contactEmail}`}
              className="font-medium transition-colors border border-primary text-primary px-4 py-2 rounded-full hover:bg-accent hover:text-white hover:border-accent active:bg-accent active:text-white active:border-accent"
            >
              Contact
            </a>
            {isAdmin && (
              <Button 
                className="bg-accent hover:bg-accent/90 text-white rounded-full"
                onClick={() => navigate('/admin')}
                data-testid="admin-button"
              >
                Admin
              </Button>
            )}
          </nav>

          {/* Mobile Menu Button */}
          <button 
            className="md:hidden"
            onClick={() => setIsMobileMenuOpen(!isMobileMenuOpen)}
            data-testid="mobile-menu-button"
          >
            {isMobileMenuOpen ? (
              <X className="text-primary" />
            ) : (
              <Menu className="text-primary" />
            )}
          </button>
        </div>
      </div>

      {/* Mobile Menu */}
      {isMobileMenuOpen && (
        <motion.div 
          initial={{ opacity: 0, y: -20 }}
          animate={{ opacity: 1, y: 0 }}
          className="md:hidden bg-white border-t border-border"
        >
          <nav className="flex flex-col p-4 gap-4">
            <a 
              href="#"
              onClick={(e) => { e.preventDefault(); navigate('/'); setIsMobileMenuOpen(false); }}
              className="font-medium border border-primary text-primary px-4 py-2 rounded-full text-center hover:bg-accent hover:text-white hover:border-accent active:bg-accent active:text-white"
            >
              Home
            </a>
            <a 
              href="#"
              onClick={(e) => { e.preventDefault(); navigate('/terms'); setIsMobileMenuOpen(false); }}
              className="font-medium border border-primary text-primary px-4 py-2 rounded-full text-center hover:bg-accent hover:text-white hover:border-accent active:bg-accent active:text-white"
            >
              Terms
            </a>
            <a 
              href="#"
              onClick={(e) => { e.preventDefault(); navigate('/privacy'); setIsMobileMenuOpen(false); }}
              className="font-medium border border-primary text-primary px-4 py-2 rounded-full text-center hover:bg-accent hover:text-white hover:border-accent active:bg-accent active:text-white"
            >
              Privacy
            </a>
            <a 
              href={`mailto:${contactEmail}`}
              className="font-medium border border-primary text-primary px-4 py-2 rounded-full text-center hover:bg-accent hover:text-white hover:border-accent active:bg-accent active:text-white"
            >
              Contact
            </a>
            {isAdmin && (
              <Button 
                className="bg-accent hover:bg-accent/90 text-white rounded-full"
                onClick={() => { navigate('/admin'); setIsMobileMenuOpen(false); }}
              >
                Admin
              </Button>
            )}
          </nav>
        </motion.div>
      )}
    </header>
  );
};

export default Header;
